/*
 * business-card/spec.js — the physical card, in millimetres.
 *
 * WHY THIS IS A MODULE AND NOT THREE CONSTANTS
 * ────────────────────────────────────────────
 * A business card has FOUR rectangles, and every print failure people describe
 * is one of them being confused with another:
 *
 *   BLEED box   — what the printer receives. Trim + bleed on every side.
 *   TRIM box    — where the guillotine is aimed. This is "90×50".
 *   SAFE box    — trim minus a margin. Text outside it gets shaved off.
 *   VISUAL box  — what the user sees on screen, which is the trim box, because
 *                 that is the object they are holding in their head.
 *
 * The guillotine has a tolerance of about ±1 mm on a stack of 500 cards, and it
 * is not centred — the whole stack drifts. That single fact is the reason bleed
 * and safe zone BOTH exist and why neither can be dropped:
 *
 *   - cut lands OUTSIDE the trim → you see whatever was in the bleed. If the
 *     background stopped at the trim line, that is bare paper: a white hairline
 *     down one edge. This is the "у меня белая полоска" complaint.
 *   - cut lands INSIDE the trim → whatever sat near the edge is gone. If the
 *     phone number was 1 mm from the trim, the last digit is now a half-digit.
 *
 * So the model here always carries all four, and the UI never lets the user
 * meet only one of them.
 *
 * BLEED IS REGIONAL, AND THE DIFFERENCE IS NOT COSMETIC. RU/CIS print shops
 * publish 2 mm (дообрезной 94×54 for a 90×50 card); the international default
 * is 3 mm; North America is 0.125 in = 3.175 mm. Handing a RU shop a 3 mm-bleed
 * file is harmless — they trim more. Handing an international shop a 2 mm file
 * gets it rejected by the automated preflight. Since we cannot know the shop,
 * the format carries its region's convention and the user can override.
 *
 * Pure module: no DOM, no browser API. `node --test` drives it directly.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardSpec = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var IN = 25.4;                       // exact: mm per inch

    /* ── formats ─────────────────────────────────────────────────────────────
     * `w`/`h` are the TRIM size in landscape orientation. Portrait is the same
     * card rotated, so it is a flag on the document and not a second entry —
     * duplicating every format as "…-vertical" is how competitors end up with
     * a 40-item dropdown that is really 20 cards.
     *
     * `bleed` is the region's published convention (see the header). `safe` is
     * the margin from the TRIM edge that the region's shops ask content to keep.
     * RU shops publish 5 mm and mean "do not put the phone number there"; the
     * international 3 mm is a hard minimum. We store a workable number per
     * region rather than inventing an average of the two.
     */
    var FORMATS = [
        /* id            w       h      bleed  safe  region   note */
        ['ru-90x50',     90,     50,    2,     4,    'ru',    'ru'],
        ['eu-85x55',     85,     55,    3,     3,    'eu',    'eu'],
        ['us-3.5x2',     3.5*IN, 2*IN,  3.175, 3.175,'us',    'us'],
        ['jp-91x55',     91,     55,    3,     3,    'jp',    'jp'],
        ['au-90x55',     90,     55,    3,     3,    'au',    'au'],
        ['iso-85.6x54',  85.6,   53.98, 3,     3,    'iso',   'iso'],
        ['sq-55x55',     55,     55,    3,     3,    'any',   'square'],
        ['sq-65x65',     65,     65,    3,     3,    'any',   'square_big'],
        ['slim-85x40',   85,     40,    3,     3,    'any',   'slim'],
        ['mini-70x28',   70,     28,    2,     2,    'any',   'mini'],
    ];

    /* Corner radius offered in the UI. A die-cut round corner costs extra at
     * every shop, so it is opt-in and the preview must show it: a layout that
     * looks fine square can lose a logo corner at 5 mm radius. */
    var RADII = [0, 2, 3, 5, 8];

    /* Below this, ink coverage stops being a design choice and becomes a
     * mechanical problem — see palette.js for what each threshold means. */
    var LIMITS = {
        /* Offset on coated stock. Above this the sheet does not dry and offsets
         * onto the next card in the stack. ISO 12647-2 coated allows 330; shops
         * publish 300 as the safe instruction, so that is what we grade at. */
        maxInkPct: 300,
        /* A stroke thinner than this disappears or goes ragged: it is at the
         * limit of what the plate can hold. 0.25 pt is the classic "hairline". */
        minStrokeMm: 0.09,       // ≈ 0.25 pt
        /* Text smaller than this is legible in a PDF viewer and not on paper. */
        minTextPt: 6,
        /* …and this is where it stops being comfortable for a phone number.
         * 7.0 rather than 7.5: small caps for a company kicker are routinely
         * set at 6.5-7 pt on professionally produced cards, and grading those
         * as a problem made the panel cry wolf on 1028 of 300 preset/format
         * combinations — a warning that fires on almost everything is a warning
         * nobody reads. Below 7 pt a phone number genuinely is uncomfortable. */
        comfortTextPt: 7.0,
        /* Effective resolution for a placed raster. 300 is the press standard;
         * below 150 it is visibly soft at arm's length. */
        goodDpi: 300,
        minDpi: 150,
        /* Rich black (CMY under the K) smears on small type because the plates
         * cannot register that tightly. Above this size it is what you want. */
        richBlackMinPt: 24,
    };

    function byId(id) {
        for (var i = 0; i < FORMATS.length; i++) if (FORMATS[i][0] === id) return format(FORMATS[i]);
        return null;
    }

    function format(row) {
        return {
            id: row[0], w: row[1], h: row[2],
            bleed: row[3], safe: row[4], region: row[5], note: row[6],
        };
    }

    function all() { return FORMATS.map(format); }

    /* ── the four rectangles ─────────────────────────────────────────────────
     * Origin is the TOP-LEFT of the TRIM box, y down. Everything the user
     * positions is in this space, so a layout survives a change of bleed: the
     * bleed box simply grows around it. Anchoring at the bleed box instead —
     * which is what a naive "the PDF page is the canvas" implementation does —
     * shifts every element by 1 mm the moment someone switches RU→EU.
     */
    function boxes(fmt, opt) {
        opt = opt || {};
        var bleed = num(opt.bleed, fmt.bleed);
        var safe  = num(opt.safe,  fmt.safe);
        var rot   = !!opt.portrait;
        var w = rot ? fmt.h : fmt.w;
        var h = rot ? fmt.w : fmt.h;
        return {
            w: w, h: h,
            bleed: bleed, safe: safe,
            /* page = the PDF page. Trim sits inset by `bleed` on every side. */
            page:  { w: w + 2 * bleed, h: h + 2 * bleed },
            trim:  { x: 0, y: 0, w: w, h: h },
            safeBox: { x: safe, y: safe, w: w - 2 * safe, h: h - 2 * safe },
            /* Where the trim box starts inside the page, in page coordinates. */
            offset: { x: bleed, y: bleed },
        };
    }

    function num(v, dflt) {
        var n = parseFloat(v);
        return (isFinite(n) && n >= 0) ? n : dflt;
    }

    /* ── how many fit on a sheet ─────────────────────────────────────────────
     * Home printing is a real destination (the "печать визиток" search intent),
     * and the number that matters is not "10 on A4" but "10 on A4 GIVEN this
     * printer cannot mark the outer 5 mm". Computed, never tabulated: a 91 mm
     * Japanese card does not fit two-up on A4 the way a 90 mm one does, and a
     * table would quietly claim it does.
     *
     * `gap` is the space between cards. Zero gap means neighbouring cards share
     * a cut line — fewer cuts, and the standard way shops impose. But it also
     * means each card's bleed overlaps its neighbour's, so a background that
     * bleeds shows up in the neighbour. Hence `gap` defaults to twice the bleed
     * when the sheet carries bleeds, and 0 when it does not.
     *
     * ⚠️⚠️ `rotated` IS A CONTRACT WITH THE CALLER, NOT A HINT. It says the
     * artwork must be turned 90° inside its slot, and `cardW`/`cardH` are
     * already the TURNED trim dimensions. A caller that reads the slot geometry
     * and draws the card upright lays 90 mm of artwork into a 50 mm column and
     * every card overlaps its neighbour. That shipped: 12 cards per A4 was
     * counted and reported correct, because the count IS correct — nobody
     * rendered the sheet and looked at it. Guard:
     * tests/playwright/business-card-sheet.mjs rasterises the sheet and asserts
     * the trim rectangles do not intersect.
     *
     * The `inset` term is each card's own bleed sticking out of its trim box.
     * It is part of the block's footprint on paper, so it belongs in both the
     * fit count and the centring; leaving it out overflows the printer's dead
     * zone by one bleed and shifts the whole block half a bleed off centre.
     */
    function imposition(fmt, opt) {
        opt = opt || {};
        var b = boxes(fmt, opt);
        var page = opt.sheet || { w: 210, h: 297 };
        var margin = num(opt.margin, 5);          // printer dead zone
        var gap = num(opt.gap, opt.bleeds ? b.bleed * 2 : 0);
        var inset = opt.bleeds ? b.bleed : 0;

        var usableW = page.w - 2 * margin - 2 * inset;
        var usableH = page.h - 2 * margin - 2 * inset;

        function fitCount(cardW, cardH) {
            /* n cards take n*card + (n-1)*gap. Solve for the largest n. */
            var cols = Math.floor((usableW + gap) / (cardW + gap));
            var rows = Math.floor((usableH + gap) / (cardH + gap));
            return { cols: Math.max(0, cols), rows: Math.max(0, rows) };
        }

        var a = fitCount(b.w, b.h);
        var bb = fitCount(b.h, b.w);            // the card turned 90°
        var rotated = (bb.cols * bb.rows) > (a.cols * a.rows);
        var g = rotated ? bb : a;
        var cw = rotated ? b.h : b.w;
        var ch = rotated ? b.w : b.h;

        var blockW = g.cols * cw + Math.max(0, g.cols - 1) * gap + 2 * inset;
        var blockH = g.rows * ch + Math.max(0, g.rows - 1) * gap + 2 * inset;

        return {
            cols: g.cols, rows: g.rows, perSheet: g.cols * g.rows,
            rotated: rotated,
            cardW: cw, cardH: ch, gap: gap, inset: inset,
            /* Centred on the sheet: an off-centre block wastes one row's worth
             * of margin on one side and is the first thing that looks amateur
             * when the sheet is held up. */
            x0: (page.w - blockW) / 2,
            y0: (page.h - blockH) / 2,
            blockW: blockW, blockH: blockH,
            sheet: page,
        };
    }

    return {
        IN: IN,
        LIMITS: LIMITS,
        RADII: RADII,
        all: all,
        byId: byId,
        boxes: boxes,
        imposition: imposition,
    };
});
